import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import axios from 'axios';

// Soft UI
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import IconButton from '@mui/material/IconButton';

import UnpublishedIcon from '@mui/icons-material/Unpublished';
import DownloadIcon from '@mui/icons-material/Download';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Swal from 'sweetalert2';

// Tu tabla
import Table from 'prefabsComponents/Tables/Table';

// Modales para agregar y editar items
import AddRowModal from '../AddRowModal';
import EditRowModal from '../EditRowModal';

// Nuevo modal para editar datos del consolidado
import EditConsolidatedModal from '../EditConsolidadoModal';

// Importamos XLSX para exportar a Excel
import * as XLSX from 'xlsx';

function ConsolidadoModal({ show, handleClose, consolidatedNumber }) {
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [showAgregar, setShowAgregar] = useState(false);

  // Editar items
  const [showEdit, setShowEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Editar datos del consolidado
  const [showEditConsolidado, setShowEditConsolidado] = useState(false);

  const columns = [
    { name: 'Acciones', align: 'center' },
    { name: '#Air Waybill', align: 'left' },
    { name: 'Customer', align: 'left' },
    { name: 'Charge', align: 'left' },
    { name: 'Cost PP', align: 'right' },
    { name: 'Cost CC', align: 'right' },
    { name: 'Income PP', align: 'right' },
    { name: 'Income CC', align: 'right' },
    { name: 'Total Waybill', align: 'right' },
    { name: 'Gross Profit', align: 'right' },
    { name: 'Agent Profit', align: 'right' },
    { name: 'Your Profit', align: 'right' },
  ];

  const refreshData = () => {
    if (consolidatedNumber) {
      axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/finance/consolidated/charges/${consolidatedNumber}/`)
        .then((response) => {
          setConsolidatedData(response.data);
        })
        .catch((error) => {
          console.error('Error al obtener detalle de consolidado:', error);
        });
    }
  };

  useEffect(() => {
    if (show && consolidatedNumber) {
      refreshData();
    }
  }, [show, consolidatedNumber]);

  // Editar un item de la tabla
  const handleEdit = (item) => {
    setSelectedRow(item);
    setShowEdit(true);
  };

  // Eliminar un item
  const handleDelete = (item) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción eliminará el item de forma permanente',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonColor: '#dc3545',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`${process.env.REACT_APP_SERVER_URL}/api/finance/consolidated/charges/${item.id_magaya}/`)
          .then((response) => {
            Swal.fire({
              icon: 'success',
              title: response.data.message,
              toast: true,
              position: 'top-end',
              timer: 3000,
              showConfirmButton: false,
            });
            refreshData();
          })
          .catch((error) => {
            console.error('Error al eliminar el item:', error);
            Swal.fire({
              icon: 'error',
              title: 'No se pudo eliminar el item, intente nuevamente.',
              toast: true,
              position: 'top-end',
              timer: 3000,
              showConfirmButton: false,
            });
          });
      }
    });
  };

  // Botón para validar el consolidado
  const handleValidateConsolidado = () => {
    if (!consolidatedNumber) return;

    Swal.fire({
      title: '¿Deseas validar este consolidado?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, validar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .post(`${process.env.REACT_APP_SERVER_URL}/api/finance/validate-consolidated/`, {
            master: consolidatedNumber,
          })
          .then((response) => {
            Swal.fire({
              icon: 'success',
              title: response.data.message,
              toast: true,
              position: 'top-end',
              timer: 3000,
              showConfirmButton: false,
            });
            refreshData();
          })
          .catch((error) => {
            console.error('Error al validar el consolidado:', error);
            Swal.fire({
              icon: 'error',
              title: 'El consolidado no fue validado, por favor intente nuevamente.',
              toast: true,
              position: 'top-end',
              timer: 3000,
              showConfirmButton: false,
            });
          });
      }
    });
  };

  // Armar filas para la tabla
  const rows = consolidatedData?.rows?.map((item) => ({
    Acciones: (
      <SoftBox display="flex" gap={1}>
        <IconButton onClick={() => handleEdit(item)} size="small" color="warning">
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => handleDelete(item)} size="small" color="error">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </SoftBox>
    ),
    '#Air Waybill': item.waybill,
    Customer: item.customer,
    Charge: item.charge,
    'Cost PP': item.costpp,
    'Cost CC': item.costcc,
    'Income PP': item.incpp,
    'Income CC': item.inccc,
    'Total Waybill': item.total_waybill,
    'Gross Profit': item.gross_profit,
    'Agent Profit': item.agent_profit,
    'Your Profit': item.your_profit,
    id_magaya: item.id_magaya,
  })) || [];

  const totalsColumns = [
    { name: 'Cost PP', align: 'center' },
    { name: 'Cost CC', align: 'center' },
    { name: 'Income PP', align: 'center' },
    { name: 'Income CC', align: 'center' },
    { name: 'Gross Profit', align: 'center' },
    { name: 'Agent Profit', align: 'center' },
    { name: 'Your Profit', align: 'center' },
    { name: 'You Owe Agent', align: 'center' },
  ];

  const totalsRows = consolidatedData?.totals
    ? [
        {
          'Cost PP': consolidatedData.totals.costpp,
          'Cost CC': consolidatedData.totals.costcc,
          'Income PP': consolidatedData.totals.incpp,
          'Income CC': consolidatedData.totals.inccc,
          'Gross Profit': consolidatedData.totals.gross_profit,
          'Agent Profit': consolidatedData.totals.agent_profit,
          'Your Profit': consolidatedData.totals.your_profit,
          'You Owe Agent': consolidatedData.totals.owe_agent,
        },
      ]
    : [];

  const handleAgregarSubmit = () => {
    refreshData();
    setShowAgregar(false);
  };

  const handleUpdateSuccess = () => {
    refreshData();
    setShowEdit(false);
  };

  // Cuando se actualiza el consolidado
  const handleConsolidatedUpdated = () => {
    refreshData();
    setShowEditConsolidado(false);
  };

  // Función para descargar la tabla en Excel
  const handleDownload = () => {
    if (!consolidatedData) return;

    // Preparamos los datos de las filas, omitiendo columnas que no sean necesarias para el Excel
    const exportRows = consolidatedData.rows.map(row => ({
      "#Air Waybill": row.waybill,
      "Customer": row.customer,
      "Charge": row.charge,
      "Cost PP": row.costpp,
      "Cost CC": row.costcc,
      "Income PP": row.incpp,
      "Income CC": row.inccc,
      "Total Waybill": row.total_waybill,
      "Gross Profit": row.gross_profit,
      "Agent Profit": row.agent_profit,
      "Your Profit": row.your_profit,
    }));

    // Creamos un libro de trabajo y agregamos una hoja con los datos
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(wb, ws, "Consolidated Data");

    // Si existen totales, los agregamos en otra hoja
    if (consolidatedData.totals) {
      const totalsData = [consolidatedData.totals];
      const wsTotals = XLSX.utils.json_to_sheet(totalsData);
      XLSX.utils.book_append_sheet(wb, wsTotals, "Totals");
    }

    // Descargamos el archivo Excel
    XLSX.writeFile(wb, `Consolidated_${consolidatedNumber}.xlsx`);
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="fullscreen"
        centered
        backdrop="static"
        style={{ zIndex: 1050 }}
      >
        <Modal.Header closeButton>
          <SoftTypography variant="h5" fontWeight="bold">
            Revisión de Consolidado #{consolidatedNumber}
          </SoftTypography>
        </Modal.Header>
        <Modal.Body>
          {consolidatedData ? (
            <>
              {/* Información de cabecera */}
              <SoftBox mb={2}>
                <SoftBox display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1} mb={2}>
                  <SoftBox display="flex" flexDirection="column" gap={1}>
                    <SoftTypography variant="button" fontWeight="medium">
                      Agente: {consolidatedData.agente}
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="medium">
                      Origen: {consolidatedData.origen}
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="medium">
                      Destino: {consolidatedData.destino}
                    </SoftTypography>
                  </SoftBox>
                  <SoftBox display="flex" flexDirection="column" gap={1}>
                    <SoftTypography variant="button" fontWeight="medium">
                      Kilogramos: {consolidatedData.kls}
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="medium">
                      Aerolínea: {consolidatedData.transportista}
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="medium">
                      Fecha: {consolidatedData.creation_date}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
                <SoftBox display="flex" alignItems="center" gap={1}>
                  {/* Si NO hay estado => No validado */}
                  {!consolidatedData?.validacion?.estado ? (
                    <SoftButton
                      color="error"
                      title="No validado"
                      onClick={() => handleValidateConsolidado(true)}>
                      <UnpublishedIcon />
                      No validado
                    </SoftButton>
                  ) : (
                    // Si está validado, permite editar datos del consolidado
                    <SoftButton
                      color="success"
                      title={consolidatedData?.validacion?.info || ''}
                      onClick={() => setShowEditConsolidado(true)}
                    >
                      <HowToRegIcon />
                      Validado
                    </SoftButton>
                  )}
                </SoftBox>
              </SoftBox>

              {/* Tabla principal */}
              <SoftBox mb={3} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                <Table columns={columns} rows={rows} />
              </SoftBox>

              {/* Botón Agregar */}
              <SoftBox mt={2} mb={3}>
                <SoftButton
                  color="info"
                  variant="gradient"
                  size="small"
                  onClick={() => setShowAgregar(true)}
                >
                  Agregar
                </SoftButton>
              </SoftBox>

              <hr />

              {/* Tabla de Totales */}
              {consolidatedData.totals && (
                <SoftBox mt={3}>
                  <Table columns={totalsColumns} rows={totalsRows} />
                </SoftBox>
              )}
            </>
          ) : (
            <SoftTypography>Cargando detalles...</SoftTypography>
          )}
        </Modal.Body>
        <Modal.Footer>
          <SoftButton color="success" variant="gradient" onClick={handleDownload}>
            <DownloadIcon /> Descargar
          </SoftButton>
        </Modal.Footer>
      </Modal>

      {/* Modal para Agregar Items */}
      <AddRowModal
        show={showAgregar}
        handleClose={() => setShowAgregar(false)}
        handleSubmit={handleAgregarSubmit}
        consolidatedNumber={consolidatedNumber}
      />

      {/* Modal para Editar Items */}
      <EditRowModal
        show={showEdit}
        onClose={() => setShowEdit(false)}
        rowData={selectedRow}
        onUpdated={handleUpdateSuccess}
      />

      {/* Modal para Editar el Consolidado */}
      <EditConsolidatedModal
        show={showEditConsolidado}
        onClose={() => setShowEditConsolidado(false)}
        masterNumber={consolidatedNumber}
        initialAgent={consolidatedData?.agente}
        initialTransportista={consolidatedData?.transportista}
        initialOrigen={consolidatedData?.origen}
        initialDestino={consolidatedData?.destino}
        onUpdated={handleConsolidatedUpdated}
      />
    </>
  );
}

ConsolidadoModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  consolidatedNumber: PropTypes.string,
};

ConsolidadoModal.defaultProps = {
  consolidatedNumber: '',
};

export default ConsolidadoModal;
